import { PLAYER } from '../core/config';
import { layoutToWorld } from '../core/coords';
import type { Layout, Landmark } from '../data/types';
import type { Vec2 } from '../geo/types';
import { pointInRing, distancePointSegment } from '../geo/polygon';
import type { CollisionWorld } from '../nav/types';
import { obstacleProbe } from '../nav/buildCollisionWorld';
import { createStreetRelief } from '../site/streetRelief';
import type { BakedCrowd } from '../world/life/crowd/crowdPlan';
import { restoreWalkNet, type WalkNet } from '../world/life/crowd/walkNet';
import { routeTree } from '../world/life/crowd/routes';
import { CHURCH_STOPS } from './churchStories';
import { guidedTargets, gazeNear, roundedPath, roundedCircuit, itemAt } from './tourPath';
import { roundReversals } from './turnaround';
import { smoothTourTargets } from './tourSight';
import { TOUR_SUMMARIES } from './tourStories';
import type { TourRoute } from './types';
import type { Obstacle } from '../world/types';

export interface ChurchRouteContext {
  readonly crowd: BakedCrowd;
  readonly collision: CollisionWorld;
  readonly obstacles: readonly Obstacle[];
}
const gap = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]);

function churchNet(
  crowd: BakedCrowd,
  point: (node: number) => Vec2,
  clear: (a: Vec2, b: Vec2) => boolean
): WalkNet {
  const edges: number[] = [];
  for (let e = 0; e < crowd.net.edges.length; e += 2) {
    const a = itemAt(crowd.net.edges, e),
      b = itemAt(crowd.net.edges, e + 1);
    if (clear(point(a), point(b)) && clear(point(b), point(a))) edges.push(a, b);
  }
  return restoreWalkNet({ nodes: crowd.net.nodes, edges });
}

function churchSight(
  church: Landmark,
  target: Vec2,
  collision: CollisionWorld,
  obstacles: readonly Obstacle[]
) {
  const blockedSight = obstacleProbe(obstacles.filter((o) => o.id !== church.id));
  return (from: Vec2): boolean => {
    const length = gap(from, target);
    for (let d = 0; d < length; d += 0.5) {
      const p: Vec2 = [
        from[0] + ((target[0] - from[0]) * d) / length,
        from[1] + ((target[1] - from[1]) * d) / length,
      ];
      if (
        pointInRing(p, church.footprint) ||
        church.footprint.some(
          (a, i) =>
            distancePointSegment(p, {
              a,
              b: itemAt(church.footprint, (i + 1) % church.footprint.length),
            }) <
            collision.radius + 0.5
        )
      )
        return true;
      if (blockedSight(p)) return false;
    }
    return true;
  };
}

function mainPiece(net: WalkNet): number {
  const sizes = new Map<number, number>();
  for (const piece of net.piece) sizes.set(piece, (sizes.get(piece) ?? 0) + 1);
  return itemAt(
    [...sizes].sort((a, b) => b[1] - a[1]),
    0
  )[0];
}

function churchWallDistance(church: Landmark, obstacles: readonly Obstacle[]) {
  const edges = obstacles
    .filter((o) => o.id === church.id)
    .flatMap((o) =>
      o.polygon.map((a, i) => ({ a, b: itemAt(o.polygon, (i + 1) % o.polygon.length) }))
    );
  return (p: Vec2): number => Math.min(...edges.map((edge) => distancePointSegment(p, edge)));
}

function churchStops(
  layout: Layout,
  net: WalkNet,
  point: (node: number) => Vec2,
  collision: CollisionWorld,
  obstacles: readonly Obstacle[]
) {
  const main = mainPiece(net);
  return CHURCH_STOPS.map((story) => {
    const church = layout.landmarks.find((l) => l.id === story.id);
    if (!church) throw new Error(`Church tour: missing ${story.id}`);
    const target: Vec2 = [
      typeof church.params['anchorX'] === 'number' ? church.params['anchorX'] : church.pos[0],
      typeof church.params['anchorY'] === 'number' ? church.params['anchorY'] : church.pos[1],
    ];
    const visible = churchSight(church, target, collision, obstacles);
    const standBack = churchWallDistance(church, obstacles);
    const preferredDistance = church.kind === 'church' ? 24 : 12;
    const candidates = Array.from({ length: net.count }, (_, i) => i)
      .filter(
        (i) =>
          net.piece[i] === main &&
          collision.contains(point(i)) &&
          gap(point(i), target) >= 8 &&
          gap(point(i), target) < (church.kind === 'church' ? 90 : 60)
      )
      .sort(
        (a, b) =>
          Math.abs(standBack(point(a)) - preferredDistance) -
            Math.abs(standBack(point(b)) - preferredDistance) ||
          gap(point(a), target) - gap(point(b), target)
      );
    const node = candidates.find((i) => visible(point(i)));
    if (node === undefined) throw new Error(`Church tour: no clear approach to ${church.name}`);
    return { ...story, church, target, node };
  });
}

type ChurchStop = ReturnType<typeof churchStops>[number];

function churchGaze(path: readonly Vec2[], node: Vec2, stop: ChurchStop) {
  return gazeNear(
    path,
    node,
    layoutToWorld(stop.target, stop.church.kind === 'church' ? 16 : 8),
    24
  );
}

function churchCircuit(
  stops: readonly ChurchStop[],
  net: WalkNet,
  point: (node: number) => Vec2,
  clear: (a: Vec2, b: Vec2) => boolean
): readonly Vec2[] {
  const nodes = [itemAt(stops, 0).node];
  for (let i = 1; i <= stops.length; i++) {
    const from = itemAt(stops, i - 1).node,
      to = itemAt(stops, i % stops.length).node;
    const tree = routeTree(net, to);
    if (!Number.isFinite(tree.distance[from])) throw new Error('Church tour: disconnected stop');
    for (let n = itemAt(tree.next, from); n >= 0; n = itemAt(tree.next, n)) nodes.push(n);
  }
  const circuit = nodes.map(point);
  circuit.pop();
  return roundReversals(circuit, clear);
}

function cornerRadii(
  points: readonly Vec2[],
  collision: CollisionWorld,
  clear: (a: Vec2, b: Vec2) => boolean
): readonly number[] {
  return points.map((p, i) => {
    const a = itemAt(points, (i + points.length - 1) % points.length),
      b = itemAt(points, (i + 1) % points.length);
    for (let radius = 1.2; radius >= 0.075; radius /= 2) {
      const corner = roundedPath([a, p, b], [0, radius, 0], 0.2);
      if (corner.every((q, k) => collision.contains(q) && (!k || clear(itemAt(corner, k - 1), q))))
        return radius;
    }
    return 0;
  });
}

function churchChapters(
  stops: readonly ChurchStop[],
  path: readonly Vec2[],
  point: (node: number) => Vec2
) {
  const distances = [0];
  for (let i = 1; i < path.length; i++)
    distances.push(itemAt(distances, i - 1) + gap(itemAt(path, i - 1), itemAt(path, i)));
  const length = itemAt(distances, distances.length - 1);
  let previous = 0;
  const chapters = stops.map((stop, i) => {
    let nearest = previous;
    if (i) {
      for (let k = previous + 1; k < path.length - 1; k++)
        if (gap(itemAt(path, k), point(stop.node)) < gap(itemAt(path, nearest), point(stop.node)))
          nearest = k;
    }
    previous = nearest;
    return {
      at: i ? itemAt(distances, nearest) / length : 0,
      title: stop.church.name,
      text: stop.text,
      source: stop.source,
    };
  });
  return { length, chapters };
}

/** The same connected ways as the townspeople, swept again for the visitor's whole body. */
export function churchRoute(
  layout: Layout,
  { crowd, collision, obstacles }: ChurchRouteContext
): TourRoute {
  const point = (node: number): Vec2 => [
    itemAt(crowd.net.nodes, node * 3),
    itemAt(crowd.net.nodes, node * 3 + 1),
  ];
  const clear = (a: Vec2, b: Vec2): boolean => {
    if (!collision.contains(a) || !collision.contains(b)) return false;
    const reached = collision.move(a, [b[0] - a[0], b[1] - a[1]]);
    return gap(reached, b) < 0.001;
  };
  const net = churchNet(crowd, point, clear);
  const stops = churchStops(layout, net, point, collision, obstacles);
  const points = churchCircuit(stops, net, point, clear);
  const path = roundedCircuit(points, cornerRadii(points, collision, clear), 0.25);
  const { length, chapters } = churchChapters(stops, path, point);
  const relief = createStreetRelief(layout);
  const height = (p: Vec2) => relief(p) + PLAYER.eyeHeight;
  const positions = path.map((p) => layoutToWorld(p, height(p)));
  return {
    id: 'churches',
    title: 'Churches and chapels',
    kind: 'walk',
    closed: true,
    path: 'linear',
    duration: Math.ceil(length / 0.9 / 60) * 60,
    summary: TOUR_SUMMARIES.churches,
    chapters,
    positions,
    targets: smoothTourTargets(
      positions,
      guidedTargets(
        path,
        height,
        1.5,
        stops.map((stop) => churchGaze(path, point(stop.node), stop)),
        true,
        true
      )
    ),
  };
}
